/* eslint-disable jsx-a11y/label-has-associated-control */
import { type FC } from "react";
import Modal from "@root/shared/components/Modal";
import { Table, Td, Th, Tr } from "@root/shared/components/Table";
import { GuidanceBox } from "@root/shared/components/GuidanceBox";
import { Icon } from "@root/shared/components/Icon";
import { Checkbox } from "@root/shared/components/Checkbox";
import { Label } from "@root/shared/components/Label";
import { Button } from "@root/shared/components/Button";
import { type ContactAcquiredModalProps } from "@clientFrontend/features/publication/components/ContactAcquiredButton/type";
import * as styles from "./styles.css";

type Props = {
  isShow: boolean;
  isChecked: boolean;
  isPossibleToSubmit: boolean;
  handleCloseModal: () => void;
  handleChangeCheckbox: () => void;
  handleRequestProfile: () => void;
} & ContactAcquiredModalProps;

type FooterContentsProps = {
  handleCloseModal: Props["handleCloseModal"];
  isPossibleToSubmit: Props["isPossibleToSubmit"];
  handleRequestProfile: Props["handleRequestProfile"];
  entryId: string;
};

export const ContactAcquiredModal: FC<Props> = ({
  isSp,
  isShow,
  name,
  isScout,
  isSalonTour,
  isPossibleToSubmit,
  handleCloseModal,
  handleChangeCheckbox,
  isChecked,
  isMultipleEntry,
  entryId,
  handleRequestProfile,
  hasInquiryMessage,
}) => (
  <Modal
    isShow={isShow}
    pcWidthType="large"
    spAppearanceType={isSp ? "bottom" : "center"}
    headerTitle="連絡先を取得して選考を開始する"
    headerTitleStyle="gray"
    onClose={handleCloseModal}
    footerContent={FooterContents({
      handleCloseModal,
      isPossibleToSubmit,
      handleRequestProfile,
      entryId,
    })}
    footerContentStyle="button"
    canOutsideClick
  >
    <div className={styles.container}>
      {isMultipleEntry !== "none" && (
        <GuidanceBox>
          <div className={styles.guidanceInner}>
            <span className={styles.IconOuter}>
              <Icon name="information" />
            </span>
            <p className={styles.guidanceText}>
              課金対象職種：
              <span className={styles.guidanceRedText}>
                複数回の応募{isMultipleEntry === "may_duplicated" && <>の可能性</>}があります。
              </span>
              金額の高い職種が適用されます。
            </p>
          </div>
        </GuidanceBox>
      )}
      <div className={styles.tableOuter}>
        <Table>
          <Tr>
            <Th width="139px">氏名</Th>
            <Td>{name}</Td>
          </Tr>
          {hasInquiryMessage && (
            <Tr>
              <Th width="139px">
                応募時の質問・
                <br className={styles.thNewLine} />
                メッセージ
              </Th>
              <Td>あり（メッセージ画面をご確認ください）</Td>
            </Tr>
          )}
          {(isScout || isSalonTour) && (
            <Tr>
              <Th width="139px">留意事項</Th>
              <Td>
                <div className={styles.confirmOuter}>
                  {isScout && isSalonTour && <ScoutAndSalonTourUserConfirmText />}
                  {isScout && !isSalonTour && <ScoutUserConfirmText />}
                  {!isScout && isSalonTour && <SalonTourUserConfirmText />}
                  <p className={styles.checkboxOuter}>
                    <Checkbox
                      id="is_confirmed"
                      checked={isChecked}
                      onChange={handleChangeCheckbox}
                    />
                    <label className={styles.label} htmlFor="is_confirmed">
                      確認した
                    </label>
                    <Label theme="red">必須</Label>
                  </p>
                </div>
              </Td>
            </Tr>
          )}
        </Table>
      </div>
    </div>
  </Modal>
);

const FooterContents: FC<FooterContentsProps> = ({
  handleCloseModal,
  isPossibleToSubmit,
  handleRequestProfile,
  entryId,
}) => (
  <div className={styles.buttonOuter}>
    <div className={styles.closeButtonOuter}>
      <Button theme="tertiary" onClick={handleCloseModal}>
        閉じる
      </Button>
    </div>
    <Button
      theme={isPossibleToSubmit ? "primary" : "disabled"}
      onClick={handleRequestProfile}
      data-ga-category="Complete"
      data-ga-action="job_entry_contacts"
      data-ga-label={entryId}
    >
      連絡先を取得
    </Button>
  </div>
);

const ScoutAndSalonTourUserConfirmText: FC = () => (
  <p>
    スカウトからの見学希望応募です。
    <br />
    <strong className={styles.emphasisText}>まずは見学の日程調整をお願いします。</strong>
  </p>
);

const ScoutUserConfirmText: FC = () => (
  <p>
    スカウトからの応募です。
    <br />
    <strong className={styles.emphasisText}>必ず面談/面接の実施をお願いしております。</strong>
  </p>
);

const SalonTourUserConfirmText: FC = () => (
  <p>
    応募者は見学を希望しています。
    <br />
    <strong className={styles.emphasisText}>まずは見学の日程調整を推奨します。</strong>
  </p>
);
